import { getPartialPaymentTip } from './partial-payment-balance';
import { Dispatch, SetStateAction } from 'react';

import { PaymentMethod, CloverPayment, PaymentMethodType } from 'types';

/**
 * Calculate the total amount of all payment methods including processing fees if enabled.
 * @param {PaymentMethod[]} paymentMethods - An array of payment methods.
 * @param {boolean} [isProcessingFeeEnabled=false] - Whether to include processing fees in calculations.
 * @returns {number} The total amount of all payment methods.
 */
export const getPaymentMethodsWithProcessingFee = (
  paymentMethods: PaymentMethod[],
  isProcessingFeeEnabled?: boolean
) =>
  paymentMethods?.reduce((acc, item) => {
    let amountWithFee = item.amount;
    if (item.type === PaymentMethodType.Credit && isProcessingFeeEnabled) {
      const processingFee = item.proccessingFee || 0;
      amountWithFee += processingFee;
    }
    return acc + amountWithFee;
  }, 0);

const appendSingleCloverPayment = <
  T extends { tip?: number; paymentMethods: PaymentMethod[] },
>({
  prev,
  payment,
  companyProcessingFees,
  isProcessingFeeEnabled,
}: {
  prev: T;
  payment: CloverPayment;
  companyProcessingFees?: number;
  isProcessingFeeEnabled?: boolean;
}): T => {
  const isDuplicateById = prev.paymentMethods.some(
    (method) => method.cloverPaymentId === payment.paymentId
  );

  if (isDuplicateById) {
    console.warn(
      '[Checkout] Skipping duplicate payment by ID:',
      payment.paymentId
    );
    return prev;
  }

  const amount = Number((payment.amount / 100).toFixed(2));
  const tip = payment?.tipAmount
    ? Number((payment.tipAmount / 100).toFixed(2))
    : undefined;

  const processingFee =
    payment?.processingFee != null
      ? Number((payment.processingFee / 100).toFixed(2))
      : Number(
          (
            (amount - (tip ?? 0)) *
            ((companyProcessingFees ?? 0) / 100)
          ).toFixed(2)
        );

  const currentTipAmount = (prev.tip ?? 0) + (tip ?? 0);

  const next = {
    ...prev,
    tip: currentTipAmount || undefined,
    paymentMethods: [
      ...prev.paymentMethods,
      {
        totalAmount: amount,
        last4CC: payment?.last4,
        amount: Number((amount - processingFee).toFixed(2)),
        cloverTipAmount: payment.tipAmount ?? 0,
        cloverCreatedTime: payment.createdTime,
        cloverAllocation: payment.issuesData?.clixAllocation,
        type: PaymentMethodType.Credit,
        cloverPaymentId: payment.paymentId,
        id: `${amount}-${PaymentMethodType.Credit}`,
        proccessingFee: isProcessingFeeEnabled ? processingFee : 0,
      },
    ],
  };
  const selection = getPartialPaymentTip(next.paymentMethods);
  return {
    ...next,
    tip: selection ? selection.desiredTip / 100 || undefined : next.tip,
  };
};

/**
 * Appends a Clover payment to the form state.
 * @param {CloverPayment} payment - The payment received from Clover.
 * @param {Dispatch<SetStateAction<T>>} setFormValues - The function to update the form state.
 * @param {number} [companyProcessingFees] - The company's processing fees percentage.
 * @param {boolean} [isProcessingFeeEnabled=false] - Whether to include processing fees in calculations.
 */
export const appendCloverPaymentToForm = <
  T extends { tip?: number; paymentMethods: PaymentMethod[] },
>({
  payment,
  setFormValues,
  companyProcessingFees,
  isProcessingFeeEnabled,
}: {
  payment: CloverPayment;
  companyProcessingFees?: number;
  isProcessingFeeEnabled?: boolean;
  setFormValues: Dispatch<SetStateAction<T>>;
}) => {
  console.log('[Checkout] Clover payment received:', payment);

  setFormValues((prev) =>
    appendSingleCloverPayment({
      prev,
      payment,
      companyProcessingFees,
      isProcessingFeeEnabled,
    })
  );
};

export const appendCloverPaymentsToForm = <
  T extends { tip?: number; paymentMethods: PaymentMethod[] },
>({
  payments,
  setFormValues,
  companyProcessingFees,
  isProcessingFeeEnabled,
}: {
  payments: CloverPayment[];
  companyProcessingFees?: number;
  isProcessingFeeEnabled?: boolean;
  setFormValues: Dispatch<SetStateAction<T>>;
}) =>
  setFormValues((prev) =>
    payments.reduce(
      (acc, payment) =>
        appendSingleCloverPayment({
          payment,
          prev: acc,
          companyProcessingFees,
          isProcessingFeeEnabled,
        }),
      prev
    )
  );

/** Keep recovery display metadata local; preserve the existing request shape. */
export const withoutCloverFormMetadata = <
  T extends { paymentMethods?: PaymentMethod[] },
>(
  data: T
): T => ({
  ...data,
  ...(data.paymentMethods
    ? {
        paymentMethods: data.paymentMethods.map(
          ({
            cloverAllocation,
            cloverTipAmount,
            cloverCreatedTime,
            ...method
          }) => method
        ),
      }
    : {}),
});

/** A confirmed void removes only that card's collected tip, not other tips. */
export const tipAfterCloverVoid = (
  tip: number | undefined,
  method: PaymentMethod
) => {
  if (method.cloverTipAmount == null) return tip;
  const remainingCents = Math.max(
    0,
    Math.round((tip ?? 0) * 100) - method.cloverTipAmount
  );
  return remainingCents ? remainingCents / 100 : undefined;
};

/** Rebuild a partial sale's tip only after the provider confirms a card void. */
export const tipAfterPartialCloverVoid = (
  methods: PaymentMethod[],
  removed: PaymentMethod
) => {
  const remaining = methods.filter(
    (method) => method.cloverPaymentId !== removed.cloverPaymentId
  );
  const partial = getPartialPaymentTip(remaining);
  const tip =
    partial?.desiredTip ??
    remaining.reduce((sum, method) => sum + (method.cloverTipAmount ?? 0), 0);
  return tip ? tip / 100 : undefined;
};
